import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Search, X } from "lucide-react";
import {
  useNavigate,
  useSearchParams,
  useLocation,
  Navigate,
} from "react-router-dom";
import SuperInvestorTabNavigation, {
  SuperInvestorTab,
} from "./SuperInvestorTabNavigation";
import InvestorGrid from "./InvestorGrid";
import CongressionalGrid from "./CongressionalGrid";
import CongressionalTrades from "./CongressionalTrades";
import InsiderTradesLandingPage from "./InsiderTradesLandingPage";
import CongressionalTradesByTicker from "./CongressionalTradesByTicker";
import { superInvestors } from "./investorsConfig";
import { congressionalMembers, createMemberSlug } from "./congressionalConfig";
import { useMembership } from "../../contexts/MembershipContext";
import { useAnalytics } from "../../hooks/useAnalytics";
import { hapticSelection } from "../../lib/haptics";
import {
  InvestorWorkspace,
  WorkspaceHeader,
  WorkspaceState,
} from "./components/InvestorWorkspace";

const SuperInvestorsPage = () => {
  const theme = useTheme();
  const showProfileSearch = useMediaQuery(theme.breakpoints.up("lg"));
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { isMembershipActive } = useMembership();
  const [search, setSearch] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);
  const ticker = searchParams.get("ticker");
  const tabParam = searchParams.get("tab");
  const section = tabParam ?? location.state?.investorSection;
  const activeTab: SuperInvestorTab =
    section === "congressional" || section === "trades"
      ? section
      : "super-investors";
  const { trackFeature } = useAnalytics();
  useEffect(() => {
    trackFeature("super_investors_viewed", { tab: activeTab });
  }, [activeTab, trackFeature]);
  useEffect(() => {
    setSearch("");
  }, [activeTab]);
  if (
    !isMembershipActive &&
    (section === "congressional" || section === "trades")
  )
    return <Navigate to="/super-investors" replace />;
  if (tabParam !== null) {
    const cleanParams = new URLSearchParams(searchParams);
    cleanParams.delete("tab");
    const search = cleanParams.toString();
    return (
      <Navigate
        to={{ pathname: "/super-investors", search }}
        state={{ investorSection: activeTab }}
        replace
      />
    );
  }
  if (!ticker && !section) return <InsiderTradesLandingPage />;
  if (ticker) return <CongressionalTradesByTicker ticker={ticker} />;
  const query = showProfileSearch ? search.trim().toLowerCase() : "";
  const investors = superInvestors.filter((investor) =>
    `${investor.name} ${investor.fund}`.toLowerCase().includes(query),
  );
  const members = congressionalMembers.filter((member) =>
    member.name.toLowerCase().includes(query),
  );
  const count =
    activeTab === "super-investors" ? investors.length : members.length;
  const profileLabel = count === 1 ? "profile" : "profiles";
  const title = {
    "super-investors": "Super investors",
    congressional: "Congressional portfolios",
    trades: "Latest Trade Disclosures",
  }[activeTab];
  const onTabChange = (tab: SuperInvestorTab) => {
    hapticSelection();
    navigate("/super-investors", { state: { investorSection: tab } });
  };
  const navigation = (
    <SuperInvestorTabNavigation
      activeTab={activeTab}
      onTabChange={onTabChange}
    />
  );
  if (activeTab === "trades")
    return (
      <InvestorWorkspace>
        <CongressionalTrades navigation={navigation} />
      </InvestorWorkspace>
    );
  return (
    <InvestorWorkspace>
      <WorkspaceHeader
        title={title}
        inlineMobileActions
        navigation={navigation}
        search={
          showProfileSearch && (
            <TextField
              size="small"
              inputRef={searchRef}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Find a profile"
              inputProps={{ "aria-label": "Find a profile" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={18} />
                  </InputAdornment>
                ),
                endAdornment: search.length > 0 && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label="Clear profile search"
                      onClick={() => {
                        setSearch("");
                        searchRef.current?.focus();
                      }}
                      sx={{
                        color: "text.secondary",
                        "@media (pointer: coarse)": { width: 44, height: 44 },
                      }}
                    >
                      <X size={16} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                width: "100%",
                "& .MuiInputBase-root": {
                  minHeight: 34,
                  "& input": { py: 0.75 },
                  borderRadius: 1.5,
                  fontSize: "0.875rem",
                },
              }}
            />
          )
        }
        actions={
          <Typography
            sx={{
              fontSize: "0.8125rem",
              color: "text.secondary",
              whiteSpace: "nowrap",
            }}
          >
            {count} {profileLabel}
          </Typography>
        }
      />
      <Box
        data-tour="super-investors-content"
        sx={{ display: "flex", flexDirection: "column", gap: 2 }}
      >
        {activeTab === "super-investors" && (
          <InvestorGrid
            investors={investors}
            onInvestorClick={(investor) => {
              hapticSelection();
              navigate(`/super-investors/${investor.cik}`);
            }}
          />
        )}
        {activeTab === "congressional" && (
          <CongressionalGrid
            members={members}
            onMemberClick={(member) => {
              hapticSelection();
              navigate(
                `/super-investors/congressional/${createMemberSlug(member.name)}`,
              );
            }}
          />
        )}
        {count === 0 && (
          <WorkspaceState message="No profiles match your search." />
        )}
      </Box>
    </InvestorWorkspace>
  );
};
export default SuperInvestorsPage;
